import { Order } from "./data/Order"
import { Spinner } from "./Spinner"
import { Toast } from "./Toast"
import { Messaging } from "./lib/Messaging"
import { I18N } from "./I18N"
import { AcceptOrder } from "./AcceptOrder"
import { RejectWaitingOrder } from "./RejectWaitingOrder"
import { PrintDialog } from "./PrintDialog"
import { Print } from "./Print"
import { WindowsAgentClient } from "./lib/WindowsAgentClient"

export class Counter {
	_target
	_stores
	_myStores
	_ordersTable
	_orders
	_toastsToDismiss = []
	_updatePausedToast
	_visibleTasks = ['inbox', 'print', 'waitForPreparation', 'prepare', 'deliver']
	_responsibilities = ['inbox', 'print', 'prepare']
	_autoPrintRequests = new Set()
	// Tasks with a print or a claim/complete in progress. Without this, auto print plus a click on the still
	// visible print button (or a double click) prints twice and the second claim hits the completed task.
	_printingTaskIds = new Set()
	_completingTaskIds = new Set()
	_agentDownloadDismissedStorageKey = 'vls_agentDownloadBannerDismissed'

	constructor(target, stores, sounds, windowsAgentSoundReadyBridge) {
		this._stores = stores
		this._sounds = sounds
		this._soundReadyBridge = windowsAgentSoundReadyBridge
		this._target = document.getElementById(target)
		this._target.setAttribute('hidden', '')
		this._target.innerHTML = `<h1><span id="confirmationSyncIcon" class="material-icons sync">sync</span><span i18n>Counter</span></h1>
			<div id="agentDownloadBanner" class="card" hidden>
				<span i18n>For dialog-free printing and an order alarm that also works while the screen is locked, install the Windows companion app.</span>
				<a id="agentDownloadLink" href="/downloads/ViertelLieferserviceAgent-Setup.exe" download i18n>Download</a>
				<button id="agentDownloadDismissBtn" class="iconButton" aria-label="Dismiss"><span class="material-icons-outlined">close</span></button>
				<div class="agentBannerHint">
					<span i18n>If Windows warns about the installer, install this certificate first (double-click it, then "Install Certificate" → Local Machine → store: Trusted People):</span>
					<a id="agentCertDownloadLink" href="/downloads/ViertelLieferserviceAgent-CodeSign.cer" download i18n>Security certificate</a>
				</div>
				<div class="agentBannerHint">
					<span i18n>If the installer is blocked, use this portable version instead: unzip it and run ViertelLieferserviceAgent.exe directly.</span>
					<a id="agentPortableDownloadLink" href="/downloads/ViertelLieferserviceAgent-Portable.zip" download i18n>Portable version</a>
				</div>
			</div>
			<button id="refreshOrdersBtn" class="primary" i18n>Refresh</button>
			<table class="priceTable">
				<thead>
					<tr>
						<td>id</td>
						<td i18n>Entry</td>
						<td i18n>Preparation start</td>
						<td i18n>Date</td>
						<td i18n>Delivery</td>
						<td i18n>Name</td>
						<td i18n>Fon</td>
						<td i18n>Items</td>
						<td i18n>Price</td>
						<td i18n>Payment</td>
						<td i18n>Store</td>
						<td i18n>Complete</td>
					</tr>
				</thead>
				<tbody id="ordersTable"></tbody>
			</table>
			<div id="receiptPreview"></div>
			<div id="acceptOrder"></div>
			<div id="rejectOrder"></div>
			<div id="printOrder"></div>
		`
		this._ordersTable = document.getElementById('ordersTable')
		document.getElementById('refreshOrdersBtn').addEventListener('click', () => this._refreshOrders())
		document.getElementById('agentDownloadDismissBtn').addEventListener('click', () => this._dismissAgentDownloadBanner())
		I18N.translateElement(this._target)
		this._maybeShowAgentDownloadBanner()
	}

	/**
	 * Shows a dismissible hint to install the optional local Windows agent (see
	 * WindowsAgentClient) - dialog-free printing and a lock-screen-proof order alarm need it,
	 * but it works fine without it too, so this is just a nudge, not a requirement.
	 *
	 * The storage key holds the user's explicit choice: 'true' = dismissed (via the close
	 * button or UserInfo's checkbox), 'shown' = forced on via UserInfo's checkbox (e.g. to
	 * download an update or the portable version while the agent is already running). Without
	 * a choice it's shown on Windows (any browser, not just one launched by the agent itself)
	 * only if the agent isn't already running.
	 */
	_maybeShowAgentDownloadBanner = () => {
		const choice = localStorage.getItem(this._agentDownloadDismissedStorageKey)
		if (choice === 'shown') {
			this._setAgentDownloadBannerHidden(false)
			return
		}
		if (choice) return
		if (!navigator.userAgent.includes('Windows')) return
		WindowsAgentClient.probe().then(agentPresent => {
			if (!agentPresent) {
				this._setAgentDownloadBannerHidden(false)
			}
		})
	}

	_dismissAgentDownloadBanner = () => this.setAgentDownloadBannerVisible(false)

	_setAgentDownloadBannerHidden = hidden => document.getElementById('agentDownloadBanner').toggleAttribute('hidden', hidden)

	isAgentDownloadBannerVisible = () => !document.getElementById('agentDownloadBanner').hasAttribute('hidden')

	/**
	 * Explicit choice from UserInfo's "show download hint" checkbox - unlike the automatic
	 * case, 'visible' shows the banner even while the agent is running.
	 */
	setAgentDownloadBannerVisible = visible => {
		localStorage.setItem(this._agentDownloadDismissedStorageKey, visible ? 'shown' : 'true')
		this._setAgentDownloadBannerHidden(!visible)
	}

	setVisible = visible => {
		if (visible) {
			this._target.removeAttribute('hidden')
		}
		else {
			this._target.setAttribute('hidden', '')
			this._toastsToDismiss.forEach(toast => toast.dismiss())
		}
	}

	/**
	 * Called on login
	 * 
	 * @param {*} getValidTokenPromise A method that returns a Promise that resolves with a valid token.
	 * 	This method is used for all Secured requests of this instance
	 * @param {*} user Userinfo
	 */
	fetchOrders = (getValidTokenPromise, user) => {
		this._getValidTokenPromise = getValidTokenPromise
		this._user = user
		this._myStores = []
		this._stores.filter(store => user.groups.some(group => {
			const storeMatch = group.match(/\/cook\/(.*)/)
			return storeMatch && storeMatch[1] === store.name
		})).forEach(store => this._myStores.push(store))
		if (!this._myStores.length) {
			new Toast('toaster', I18N.translate('You are not authorized for any store. Please contact the administrator at support@viertel-lieferservice.de to receive authorizations.'), Toast.WARN)
			return
		}
		this._refreshOrders()
	}

	/**
	 * Called on login.
	 * Subscribes for new order notifications.
	 * 
	 * @param {*} getValidTokenPromise 
	 */
	subscribeToStoreCounterEvents = (getValidTokenPromise, user) => {
		this._getValidTokenPromise = getValidTokenPromise
		this._user = user
		// Tracked so _refreshOrders can wait for the WebSocket to actually be
		// connected before trying to (re)subscribe to per-order updates,
		// instead of racing against it (see fetchOrders, called in parallel
		// with this method right after login).
		this._messagingReadyPromise = this._getValidTokenPromise()
			.then(token => {
				if (!this._messaging) {
					this._messaging = new Messaging()
					this._messaging.setGetValidTokenPromise(getValidTokenPromise)
				}
				return this._messaging.connect(
					() => {
						this._updatePausedToast = new Toast('toaster', I18N.translate('The automatic update will continue...'), Toast.WARN)
						this._toastsToDismiss.push(this._updatePausedToast)
						document.getElementById('confirmationSyncIcon').classList.remove('animated')
						this._soundReadyBridge.setReady(false)
					},
					() => {
						// Re-Authorize subscriptions
						// Get a new token because the original one can be expired when resuming WebSocket
						this._getValidTokenPromise()
							.then(token => {
								// TODO provide token for subscription restoration
								document.getElementById('confirmationSyncIcon').classList.add('animated')
								if (this._updatePausedToast) {
									this._updatePausedToast.dismiss()
								}
								this._soundReadyBridge.setReady(true)
							})
					}
				)
					.then(connected => {
						this._subscriptionRequestKey = this._getUuid()
						document.getElementById('confirmationSyncIcon').classList.add('animated')
						this._soundReadyBridge.start()
						// Subscribe for events for Counter (new Orders)
						this._myStores.map(store => store.id).forEach(storeId => {
							this._messaging.subscribeWithToken(`/store/${storeId}/orders`, token, newOrderMessage => {
								// Message for cook
								console.debug('The new order message: ', newOrderMessage)
								const processInstanceId = newOrderMessage.processInstanceId
								// Subscribe to the updates of the new order
								this._subscribeToProcessIntanceId(processInstanceId)
								// Fetch new order and add it to the table
								this._fetchNewOrder(processInstanceId)
									.then(order => {
										this._orders.push(order)
										this._renderTable()
									})
							}, this._getValidTokenPromise)
						})
					})
			})
		return this._messagingReadyPromise
	}

	/**
	 * Closes WebSocket messaging on logout
	 */
	unSubscribeToStoreCounterEvents = () => {
		if (this._messaging) {
			this._messaging.close()
		}
		this._soundReadyBridge.stop()
	}

	/**
	 * Fetch all ordes from camunda and (re)create order instances
	 */
	_refreshOrders = () => {
		const spinner = new Spinner(this._target, 15)
		this._getValidTokenPromise()
			.then(token => {
				const promises = []
				this._myStores.map(store => store.id).forEach(storeId => {
					// Fetch all current orders
					promises.push(fetch(`/orders/store/${storeId}`, {
						method: 'GET',
						mode: 'cors', // no-cors, *cors, same-origin
						cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
						credentials: 'same-origin', // include, *same-origin, omit
						headers: {
							'Content-Type': 'application/json',
							'Authorization': 'Bearer ' + token
						},
						referrerPolicy: 'no-referrer' // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
					}))
				})
				Promise.all(promises)
					.then(processInstanceResponses => {
						if (processInstanceResponses && processInstanceResponses.length) {
							if (processInstanceResponses.every(i => i.ok === true)) {
								const promises2 = []
								processInstanceResponses.forEach(processInstanceResponse => promises2.push(processInstanceResponse.json()))
								Promise.all(promises2)
									.then(processInstancess => {
										const fetchOrderDetailPromises = []
										processInstancess.forEach(processInstances => {
											console.debug('Current orders are', processInstances)
											processInstances.forEach(processInstance => {
												fetchOrderDetailPromises.push(this._fetchNewOrder(processInstance.id))
											})
										})
										Promise.all(fetchOrderDetailPromises)
											.then(orders => {
												this._orders = orders
												this._renderTable()
												spinner.dismiss()
												// Wait until the WebSocket is actually connected before touching
												// this._messaging - it may still be connecting at this point
												// (this method can run in parallel with subscribeToStoreCounterEvents,
												// e.g. right after login), and subscribing too early used to fail
												// silently, leaving already-loaded orders never subscribed to
												// their /order/{id}/updates topic (missed cancellations etc.)
												;(this._messagingReadyPromise || Promise.resolve())
													.then(() => {
														// Unsubscribe from old Order events (83eac5d8-f5e5-11ec-ae2d-606720b3f224)
														this._messaging.getSubscriptions().filter(topic => topic.match(/\/order\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/updates/)).forEach(topic => {
															this._messaging.unSubscribe(topic)
														})
														// Subscribe to updates for old orders
														this._orders.forEach(order => this._subscribeToProcessIntanceId(order._id))
													})
													.catch(e => console.warn('Failed to subscribe to order updates - messaging never connected', e))
											})
									})
									.catch(e2 => console.log('Failed to parse secret', e2, processInstanceResponses))
							}
							else {
								console.warn('Failed to fetch orders of stores!')
							}
						}
					})
					.catch(e1 => console.log('Failed to fetch orders for stores', e1))
			})
			.catch(e => console.warn(new Date(), 'Failed to refresh OAuth token before call!', e))
	}

	/**
	 * Fetches an order of a known processInstanceId
	 *
	 * @param {*} processInstanceId
	 * @returns A Promise that resolves with a new order object
	 */
	_fetchNewOrder = processInstanceId => {
		return this._fetchOrderDetails(processInstanceId)
			.then(([tasksResponse, variablesResponse]) => {
				console.debug('Order details: ', tasksResponse, variablesResponse)
				const order = new Order(processInstanceId)
				order.setTasks(tasksResponse)
				if (order.getTasks().some(t => t.key === 'inbox')) {
					this._sounds.startRinging()
				}
				order.setVariables(variablesResponse)
				return order
			})
	}

	/**
	 * Fetches variables and tasks of an order with known processInstanceId
	 * @param {*} processInstanceId
	 * @returns Returns a Promise that resolves to an array with [tasks, variables]
	 */
	_fetchOrderDetails = async processInstanceId => {
		const [tasksResponse, variablesResponse] = await Promise.all([
			fetch(`/engine-rest/task?processInstanceId=${processInstanceId}`, {
				method: 'GET',
				mode: 'cors',
				cache: 'no-cache',
				credentials: 'same-origin',
				referrerPolicy: 'no-referrer'
			}),
			fetch(`/engine-rest/process-instance/${processInstanceId}/variables`, {
				method: 'GET',
				mode: 'cors',
				cache: 'no-cache',
				credentials: 'same-origin',
				referrerPolicy: 'no-referrer'
			})
		])
		if (!tasksResponse.ok) {
			throw new Error(`Failed to fetch tasks: ${tasksResponse.status}`)
		}
		if (!variablesResponse.ok) {
			throw new Error(`Failed to fetch variables: ${variablesResponse.status}`)
		}
		const [tasks, variables] = await Promise.all([tasksResponse.json(), variablesResponse.json()])
		if (!Array.isArray(tasks)) {
			throw new Error(`Unexpected tasks response: ${JSON.stringify(tasks)}`)
		}
		return [tasks, variables]
	}

	_subscribeToProcessIntanceId = processInstanceId => {
		this._messaging.subscribe(`/order/${processInstanceId}/updates`, instanceUpdateMessage => {
			// For order
			console.log('The instance update message', processInstanceId, instanceUpdateMessage)
			const oldOrder = this._orders.find(order => order._id === processInstanceId)
			if (oldOrder) {
				if (instanceUpdateMessage.completed === 'true') {
					console.log('Process instance completed: ', processInstanceId)
					this._orders = this._orders.filter(order => order._id !== processInstanceId)
					this._renderTable()
					this._messaging.unSubscribe(`/order/${processInstanceId}/updates`)
				}
				else if (instanceUpdateMessage.cancelled === 'true') {
					console.log('Process instance cancelled: ', processInstanceId)
					if (oldOrder.wasAccepted() && !oldOrder.wasSelfCancelled()) {
						// Keep the order visible instead of just removing it, so staff don't
						// accidentally keep preparing/delivering an order that was cancelled
						// after acceptance. Only applies to cancellations coming from elsewhere
						// (e.g. the customer) - if staff triggered this themselves via the
						// reject dialog, they already know and the order can just disappear.
						oldOrder.setCancelled(true)
						this._sounds.playCancelledAlert()
						this._renderTable()
					}
					else {
						this._orders = this._orders.filter(order => order._id !== processInstanceId)
						this._renderTable()
					}
					this._messaging.unSubscribe(`/order/${processInstanceId}/updates`)
				}
				else {
					// General update of order
					this._fetchOrderDetails(processInstanceId)
						.then(([tasksResponse, variablesResponse]) => {
							oldOrder.setTasks(tasksResponse)
							oldOrder.setVariables(variablesResponse)
							this._renderTable()
							if (oldOrder.getTasks().some(t => t.key === 'inbox')) {
								this._sounds.startRinging()
							}
						})
						.catch(e => {
							console.error('Failed to fetch order details after update, refreshing all orders:', processInstanceId, e)
							this._refreshOrders()
						})
				}
				// Could stop ringing
				if (!this._orders.some(o => o.getTasks().some(t => t.key === 'inbox'))) {
					this._sounds.stopRinging()
				}
			}
			else {
				console.warn('Message for unknown processInstanceId!', processInstanceId)
			}
		})
		console.log('Hey, you have subscribed for updates of processInstance: ', processInstanceId)
	}

	/**
	 * Personal trägt nach, ob eine "bei Übergabe"-Bestellung bar oder per EC
	 * bezahlt wurde (siehe Order.isPayAtHandover). Wird direkt in fair_invoices
	 * gebraucht, um bei Selbstlieferern zu unterscheiden, ob der Store das Geld
	 * selbst hat (Bar oder eigenes EC-Gerät) oder der Lieferservice (dessen
	 * eigenes EC-Gerät).
	 */
	_setPaymentType = (orderObject, paymentType) => {
		if (orderObject.getPaymentMethod() === paymentType) return
		this._getValidTokenPromise()
			.then(token => fetch(`/billing/entries/${orderObject._id}/payment-type`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				body: JSON.stringify({ paymentType }),
			}))
			.then(res => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`)
				orderObject.setPaymentMethod(paymentType)
				this._renderTable()
			})
			.catch(e => {
				console.error('Failed to update payment type:', e)
				new Toast('toaster', I18N.translate('Failed to save payment type'), Toast.WARN)
			})
	}

	/**
	 * render all current orders
	 */
	_renderTable = () => {
		this._ordersTable.innerHTML = ''
		this._orders
			.sort((a, b) => a._receiveDateTime - b._receiveDateTime)
			.sort((a, b) => a._startCookingDateTime - b._startCookingDateTime)
			.sort((a, b) => a._taskPriority - b._taskPriority)
			.forEach(orderObject => {
				const store = this._stores.find(s => s.id === orderObject.getStoreId())
				const receiptButtonId = `previewReceiptButton${orderObject.getIdString()}`
				const trElement = document.createElement('tr')
				trElement.innerHTML = `
					<td>${orderObject.getInvoiceNumber() ? orderObject.getInvoiceNumber() : orderObject.getIdString()}</td>
					<td>${orderObject.getReceiveDateTimeString()}</td>
					<td>${orderObject.getStartCookingDateTime()}</td>
					<td>${orderObject.getDeliveryDateTime()}</td>
					<td>${orderObject.getDeliveryAddressString()}</td>
					<td>${orderObject.getCustomerNameString()}</td>
					<td>${orderObject.getCustomerPhone()}</td>
					<td class=""><button id="${receiptButtonId}" type="button" class="primary ellipsis limitWidth">${orderObject.getCartItemsLabel()}</button></td>
					<td class="priceColumn">${orderObject.getPrice()}</td>
					<td>${orderObject.isPayAtHandover()
						? `<span class="paymentTypeToggle">
								<button type="button" class="paymentTypeBtn ${orderObject.getPaymentMethod() !== 'ec' ? 'active' : ''}" id="paymentCash${orderObject.getIdString()}">${I18N.translate('Cash')}</button>
								<button type="button" class="paymentTypeBtn ${orderObject.getPaymentMethod() === 'ec' ? 'active' : ''}" id="paymentEc${orderObject.getIdString()}">EC</button>
							</span>`
						: orderObject.getPaymentMethod()}</td>
					<td>${orderObject.getStoreName()}</td>
					<td>${orderObject.isCancelled()
						? `<button id="acknowledgeCancelledButton${orderObject.getIdString()}" class="cancelledBadge">${I18N.translate('Cancelled')}</button>`
						: orderObject.getTasks().filter(task => this._visibleTasks.includes(task.key)).map(task => `<button id="completeTaskButton${task.id.substring(0, 8)}" class="${this._responsibilities.includes(task.key) ? 'primary' : ''}" task="${task.key}"${this._isTaskBusy(task.id) ? ' disabled' : ''}>${task.key === 'waitForPreparation' ? I18N.translate('Reject') : task.name}</button>`).join('')}</td>
				`
				this._ordersTable.appendChild(trElement)
				if (orderObject.isPayAtHandover()) {
					['cash', 'ec'].forEach(paymentType => {
						document.getElementById(`payment${paymentType === 'cash' ? 'Cash' : 'Ec'}${orderObject.getIdString()}`)
							.addEventListener('click', () => this._setPaymentType(orderObject, paymentType))
					})
				}
				if (orderObject.isCancelled()) {
					// Clicking acknowledges the cancellation and removes the entry from the table
					document.getElementById(`acknowledgeCancelledButton${orderObject.getIdString()}`).addEventListener('click', () => {
						this._orders = this._orders.filter(order => order._id !== orderObject._id)
						this._renderTable()
					})
				}
				// Add complete task buttons (not for cancelled orders - buttons aren't rendered for those)
				if (!orderObject.isCancelled()) {
					orderObject.getTasks().filter(task => this._visibleTasks.includes(task.key)).forEach(task => {

					/**
					 * Function to complete a task
					 * 
					 * @param {*} clickedBtn Spinner traget and button to disable while completing task
					 * @param {*} variables Values to send to BPE when completing task
					 * @param {*} printReceipt Auto print checkbox checked. Set this in waiting state to print receipt when the Print Button appears.
					 */
					const completeTaskAction = (clickedBtn, variables, printReceipt) => {
						if (this._completingTaskIds.has(task.id)) {
							console.debug('Task is already being completed:', task.key, task.id)
							return
						}
						this._completingTaskIds.add(task.id)
						let completeSpinner = null
						const taskBtn = clickedBtn
						if (taskBtn) {
							taskBtn.setAttribute('disabled', '')
							completeSpinner = new Spinner(taskBtn, 7)
						}
						if (printReceipt) {
							this._autoPrintRequests.add(orderObject.getIdString())
						}
						if (task.key === 'inbox' && variables && (variables.accepted === 'accepted' || variables.accepted === 'cancelled')) {
							// Auch bei direkter Ablehnung im Eingang (noch vor Annahme) einen Receipt
							// anlegen, damit /myorders (Kundenclient) die Bestellung überhaupt findet -
							// sonst existiert nie ein Datensatz und die Bestellung bleibt dort ewig als
							// "laufend" hängen. wasAccepted=false markiert das für die Anzeige (Kunde
							// sieht "Abgelehnt" statt "Storniert"). Das anschließende Stornieren im
							// BPMN-Prozess (Notification-SendTask) setzt cancelled=true nach - dieselbe
							// POST /publish-Route wie bei jeder anderen Stornierung.
							fetch('/receipt', {
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify({
									processInstanceId: orderObject._id,
									storeId: orderObject._storeId,
									totalAmount: (orderObject._net || 0) + (orderObject._tax || 0),
									wasAccepted: variables.accepted === 'accepted',
									orderData: {
										receiveDate: orderObject._receiveDateTime,
										deliveryDate: orderObject._deliveryDateTime,
										contact: orderObject._contact,
										delivery: orderObject._delivery,
										deliveryAddress: orderObject._deliveryAddress,
										storeId: orderObject._storeId,
										storeContact: orderObject._storeContact,
										storeAddress: store ? store.address : null,
										cart: orderObject._cart,
										net: orderObject._net,
										tax: orderObject._tax,
										payment: orderObject._payment,
										remarks: orderObject._remarks,
										discounts: orderObject._discounts,
										fees: orderObject._fees,
										invoiceNumber: orderObject._invoiceNumber,
										storeReceiptImageUrl: store ? store.receiptImageUrl : null,
									}
								})
							}).catch(e => console.error('Failed to save receipt:', e))
						}
						this._getValidTokenPromise()
							.then(token => {
								console.debug('Completing Task:', task.key, task.id)
								const fetchOpts = {
									method: 'POST',
									mode: 'cors',
									cache: 'no-cache',
									credentials: 'same-origin',
									referrerPolicy: 'no-referrer',
									headers: {
										'Content-Type': 'application/json',
										'Authorization': 'Bearer ' + token
									}
								}
								fetch(`/engine-rest/task/${task.id}/claim`, {
									...fetchOpts,
									body: `{"userId": "${this._user.username}"}`
								})
									.then(claimRes => {
										if (!claimRes.ok) throw new Error(`Claim failed: ${claimRes.status}`)
										const completeBody = { variables: {} }
										if (variables) {
											Object.entries(variables).forEach(([k, v]) => {
												completeBody.variables[k] = { type: typeof v === 'number' ? 'Integer' : 'String', value: v, valueInfo: {} }
											})
										}
										return fetch(`/engine-rest/task/${task.id}/complete`, {
											...fetchOpts,
											body: JSON.stringify(completeBody)
										})
									})
									.then(completeRes => {
										if (!completeRes.ok) throw new Error(`Complete failed: ${completeRes.status}`)
										if (completeSpinner) {
											completeSpinner.dismiss()
										}
										console.log('Task completed: ', task.name)
									})
									.catch(e => {
										if (completeSpinner) {
											completeSpinner.dismiss()
										}
										console.error('Failed to claim or complete task: ', task.name, e)
										new Toast('toaster', I18N.translate('The task could not be claimed or completed due to technical reasons'), Toast.ERROR)
										if (taskBtn) {
											taskBtn.removeAttribute('disabled')
										}
										this._releaseTask(task.id)
									})
							})
					}

					/**
					 * Prints the receipt and completes the print task afterwards. Ignored while a print or
					 * completion of this task is already running.
					 *
					 * @param {*} taskBtn Clicked print button, null for auto print
					 */
					const startPrint = taskBtn => {
						if (this._isTaskBusy(task.id)) {
							console.debug('Print already in progress for task', task.id)
							return
						}
						this._printingTaskIds.add(task.id)
						const btn = taskBtn || document.getElementById(`completeTaskButton${task.id.substring(0, 8)}`)
						if (btn) {
							btn.setAttribute('disabled', '')
						}
						const print = new Print(orderObject, store, completeTaskAction, taskBtn, () => this._releaseTask(task.id))
						print.print()
					}

					// Print if autoprint is set
					if (this._autoPrintRequests.has(orderObject.getIdString()) && task.key === 'print') {
						this._autoPrintRequests.delete(orderObject.getIdString())
						startPrint(null)
					}
					// Event listeners for Task buttons
					document.getElementById(`completeTaskButton${task.id.substring(0, 8)}`).addEventListener('click', ev => {
						const taskBtn = ev.currentTarget
						if (taskBtn.getAttribute('task') === 'inbox') {
							// Open accept dialog
							const acceptDialog = new AcceptOrder('acceptOrder', orderObject, store, completeTaskAction, taskBtn)
							acceptDialog.setVisible(true)
						}
						else if (taskBtn.getAttribute('task') === 'print') {
							// Open print dialog
							// const printDialog = new PrintDialog('printOrder', orderObject, store, completeTaskAction, taskBtn)
							// printDialog.setVisible(true)
							// Silent print
							startPrint(taskBtn)
						}
						else if (taskBtn.getAttribute('task') === 'waitForPreparation') {
							// Open reject-reason dialog - completing this task cancels the order.
							// Mark it as self-cancelled so the cancellation alert sound (triggered
							// once the resulting websocket update arrives) doesn't fire for the
							// staff member who just triggered it themselves.
							const rejectAndMarkSelfCancelled = (btn, variables, printReceipt) => {
								orderObject.setSelfCancelled(true)
								completeTaskAction(btn, variables, printReceipt)
							}
							const rejectDialog = new RejectWaitingOrder('rejectOrder', rejectAndMarkSelfCancelled, taskBtn)
							rejectDialog.setVisible(true)
						}
						else {
							// Just complete other tasks
							completeTaskAction(taskBtn)
						}
					})

				})
				}
				// Add handler to preview receipt button
				const previewReceiptButton = trElement.querySelector(`#${receiptButtonId}`)
				previewReceiptButton.addEventListener('click', () => {
					const printDialog = new PrintDialog('receiptPreview', orderObject, store)
					printDialog.setVisible(true)
				})
			})
	}

	_isTaskBusy = taskId => this._printingTaskIds.has(taskId) || this._completingTaskIds.has(taskId)

	// Allows printing/completing the task again after a failure and enables its button in the current table
	_releaseTask = taskId => {
		this._printingTaskIds.delete(taskId)
		this._completingTaskIds.delete(taskId)
		const btn = document.getElementById(`completeTaskButton${taskId.substring(0, 8)}`)
		if (btn) {
			btn.removeAttribute('disabled')
		}
	}

	_getUuid = () => {
		// Replace random crypto.randomUUID()
		const array = new Uint32Array(4)
		crypto.getRandomValues(array)
		const hex = array[0].toString(16) + array[1].toString(16) + array[2].toString(16) + array[3].toString(16)
		const uuid = hex.substring(0, 8) + '-' + hex.substring(8, 12) + '-' + hex.substring(12, 16) + '-' + hex.substring(16, 20) + '-' + hex.substring(20, 32)
		return uuid
	}

}